import { useEffect, useState } from "react";
import { getAccessToken } from "../../storage/storage";
import axios from "axios";
import { FiRefreshCw, FiChevronDown, FiChevronUp } from "react-icons/fi";
import { MdOutlineWebhook } from "react-icons/md";

const STATUS_STYLES = {
  processed: "bg-green-100 text-green-700",
  failed: "bg-red-100 text-red-700",
  error: "bg-red-100 text-red-700",
  pending: "bg-yellow-100 text-yellow-700",
  received: "bg-blue-100 text-blue-700",
};

const getStatusStyle = (status) => {
  if (!status) return "bg-gray-100 text-gray-600";
  return STATUS_STYLES[status.toLowerCase()] || "bg-gray-100 text-gray-600";
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const WebhookLogSkeleton = () =>
  Array.from({ length: 6 }).map((_, i) => (
    <tr key={i} className="animate-pulse border-b border-gray-100">
      {Array.from({ length: 7 }).map((_, j) => (
        <td key={j} className="px-4 py-4">
          <div className="h-3 bg-gray-200 rounded-full w-20" />
        </td>
      ))}
    </tr>
  ));

export default function WebhookLogs() {
  const token = getAccessToken();
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [expandedRow, setExpandedRow] = useState(null);

  const fetchLogs = () => {
    setLoading(true);
    axios
      .get(`${BASE_URL}/api/webhooks/lodgify/logs/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        setLogs(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching system change Logs:", error);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const filteredLogs = logs.filter((log) => {
    const query = search.toLowerCase();
    const propertyName = log.payload_raw?.booking?.property_name || "";
    const matchesSearch =
      log.event_type?.toLowerCase().includes(query) ||
      propertyName.toLowerCase().includes(query) ||
      log.lodgify_booking_id?.includes(query) ||
      log.user_email?.toLowerCase().includes(query) ||
      String(log.id).includes(query);

    const matchesStatus =
      statusFilter === "All" ||
      log.status?.toLowerCase() === statusFilter.toLowerCase();

    return matchesSearch && matchesStatus;
  });

  const statusOptions = ["All", "Processed", "Failed", "Pending", "Received", "Error"];

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-[#FFF5CE] rounded-lg">
            <MdOutlineWebhook className="text-2xl text-[#586989]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[#586989]">System Change Logs</h1>
            <p className="text-xs text-gray-500">Lodgify integration activity</p>
          </div>
        </div>
        <button
          onClick={fetchLogs}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#586989] border border-[#586989] rounded-lg hover:bg-[#FFF5CE] transition-colors disabled:opacity-50 self-start sm:self-auto"
        >
          <FiRefreshCw className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <input
          type="text"
          placeholder="Search by event, property, booking ID, or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#586989]/30"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#586989]/30 bg-white"
        >
          {statusOptions.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 text-left">
                <th className="px-4 py-3 font-semibold">#</th>
                <th className="px-4 py-3 font-semibold">Event Type</th>
                <th className="px-4 py-3 font-semibold">Booking ID</th>
                <th className="px-4 py-3 font-semibold">Property</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Received At</th>
                <th className="px-4 py-3 font-semibold">Details</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <WebhookLogSkeleton />
              ) : filteredLogs.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-16 text-center text-gray-400">
                    <MdOutlineWebhook className="text-4xl mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No system change logs found</p>
                  </td>
                </tr>
              ) : (
                filteredLogs.map((log) => {
                  const propertyName = log.payload_raw?.booking?.property_name;
                  const guest = log.payload_raw?.guest;
                  const booking = log.payload_raw?.booking;
                  const isExpanded = expandedRow === log.id;

                  return (
                    <>
                      <tr
                        key={log.id}
                        className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-4 py-3 text-gray-400 text-xs">{log.id}</td>
                        <td className="px-4 py-3 font-medium text-gray-800 capitalize">
                          {log.event_type?.replace(/_/g, " ") || "—"}
                        </td>
                        <td className="px-4 py-3 text-gray-500 font-mono text-xs">
                          {log.lodgify_booking_id || "—"}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {propertyName || `Property #${log.lodgify_property_id}` || "—"}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${getStatusStyle(log.status)}`}>
                            {log.status_display || log.status || "unknown"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                          {formatDate(log.received_at)}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => setExpandedRow(isExpanded ? null : log.id)}
                            className="flex items-center gap-1 text-[#586989] hover:text-[#3d4f63] text-xs font-medium"
                          >
                            {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                            {isExpanded ? "Hide" : "View"}
                          </button>
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr key={`${log.id}-expanded`}>
                          <td colSpan={7} className="bg-gray-50 border-b border-gray-200 px-6 py-5">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                              {/* Meta info */}
                              <div className="space-y-3">
                                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Log Info</h4>
                                <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-2 text-xs">
                                  <InfoRow label="User" value={log.user_email} />
                                  <InfoRow label="IP Address" value={log.ip_address} />
                                  <InfoRow label="Processed At" value={formatDate(log.processed_at)} />
                                  <InfoRow label="Lodgify Property ID" value={log.lodgify_property_id} />
                                </div>

                                {guest && (
                                  <>
                                    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider pt-1">Guest</h4>
                                    <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-2 text-xs">
                                      <InfoRow label="Name" value={guest.name} />
                                      <InfoRow label="Email" value={guest.email} />
                                      <InfoRow label="Phone" value={guest.phone_number} />
                                      <InfoRow label="Country" value={guest.country} />
                                    </div>
                                  </>
                                )}

                                {booking && (
                                  <>
                                    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider pt-1">Booking</h4>
                                    <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-2 text-xs">
                                      <InfoRow label="Status" value={booking.status} />
                                      <InfoRow label="Source" value={booking.source_text || booking.source} />
                                      <InfoRow label="Arrival" value={formatDate(booking.date_arrival)} />
                                      <InfoRow label="Departure" value={formatDate(booking.date_departure)} />
                                      <InfoRow label="Nights" value={booking.nights} />
                                      <InfoRow label="Currency" value={booking.currency_code} />
                                    </div>
                                  </>
                                )}

                                {log.error_message && (
                                  <>
                                    <h4 className="text-xs font-bold text-red-500 uppercase tracking-wider pt-1">Error</h4>
                                    <pre className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap">
                                      {log.error_message}
                                    </pre>
                                  </>
                                )}
                              </div>

                              {/* Raw payload */}
                              <div className="space-y-3">
                                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Raw Payload</h4>
                                <pre className="text-xs text-gray-700 bg-white border border-gray-200 rounded-lg p-4 overflow-x-auto whitespace-pre-wrap max-h-96 overflow-y-auto">
                                  {JSON.stringify(log.payload_raw, null, 2)}
                                </pre>
                              </div>

                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {!loading && filteredLogs.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-100 text-xs text-gray-400">
            Showing {filteredLogs.length} of {logs.length} log{logs.length !== 1 ? "s" : ""}
          </div>
        )}
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-gray-400 shrink-0">{label}</span>
      <span className="text-gray-700 text-right font-medium">{value ?? "—"}</span>
    </div>
  );
}
